import { faClose } from '@fortawesome/free-solid-svg-icons/faClose'
import { faImage } from '@fortawesome/free-solid-svg-icons/faImage'
import { Box, FormLabel, Input, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { getSurveyImageSrc } from '@tm/client-app/src/common/image'
import useI18N from '@tm/client-form/src/hooks/useI18n'
import { CustomValue } from '@tm/shared-lib/src/api'
import { FontAwesomeSvgIcon } from '@tm/ui/composite/FontAwesomeSvgIcon'

import { ImageInputProps } from './types'

function ImagePreview(props: { file?: File | null; value?: CustomValue }) {
  const preview = props.file
    ? URL.createObjectURL(props.file)
    : typeof props.value === 'string'
    ? getSurveyImageSrc(props.value)
    : null

  if (!preview) return null
  return (
    <Box
      component="img"
      src={preview}
      sx={{
        display: 'block',
        height: 100,
        width: '100%',
        objectFit: 'contain',
      }}
    />
  )
}

export function DefaultImageInput(props: ImageInputProps) {
  const { parentProps, fileFieldState, fileTypes, file } = props
  const { value, field, isSubmitting } = parentProps
  const { t } = useI18N()
  const theme = useTheme()

  const { error, dragOver, onFileChange, onDragOver, onDragLeave, onDrop, clearFieldValue, maxSizeText } =
    fileFieldState

  const hasPreview = !!file || !!value
  return (
    <Stack
      sx={{
        padding: 1,
        position: 'relative',
        borderRadius: 1,
        boxShadow: dragOver ? `inset 0 0 0 2px ${theme.palette.info.main}` : '',
      }}
      data-testid="default-image-input">
      <Stack onDragEnter={onDragOver} onDragLeave={onDragLeave} onDragOver={onDragOver} onDrop={onDrop}>
        <Input
          inputProps={{
            accept: fileTypes,
          }}
          sx={{ display: 'none' }}
          id={`file-input-${field.name}`}
          type="file"
          disabled={isSubmitting}
          onChange={onFileChange}
        />
        {hasPreview && (
          <Box sx={{ height: 100, position: 'relative' }}>
            <ImagePreview file={file} value={value} />
          </Box>
        )}
        {hasPreview ? (
          <FontAwesomeSvgIcon
            icon={faClose}
            onClick={clearFieldValue}
            data-testid="file-clear-value"
            sx={{
              cursor: 'pointer',
              backgroundColor: '#0000007a',
              color: 'white',
              borderRadius: '50%',
              m: 0,
              position: 'absolute',
              top: 8,
              right: 8,
            }}
          />
        ) : (
          <Stack gap={1} p={{ xs: 1 }} alignItems="center" height={100} justifyContent="center">
            <Box sx={{ pointerEvents: 'none' }}>
              <FontAwesomeSvgIcon
                icon={faImage}
                sx={{
                  fontSize: 20,
                  color: 'text.secondary',
                }}
              />
            </Box>
            <Stack alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {t('file.dropFileHere')}{' '}
                <FormLabel htmlFor={`file-input-${field.name}`}>
                  <Typography
                    component="a"
                    role="button"
                    variant="body2"
                    color="primary.main"
                    sx={{ cursor: 'pointer', textDecoration: 'underline' }}>
                    {t('file.browse')}
                  </Typography>
                </FormLabel>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {fileTypes} / max. {maxSizeText}mb
              </Typography>
            </Stack>
          </Stack>
        )}
        {!!error.length && (
          <Typography variant="caption" color="error.main">
            {error}
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}
